import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import history from '@history';
import { Box, useTheme } from '@mui/material';
import {
  AUS_FINDINGS_ROUTING_PARAMS,
  LoanAusFindingsPaths,
} from 'app/configs/routesConfig/ausFindings';
import ContentLayout from 'app/layouts/DefaultLayout/shared/ContentLayout';
import { documentsApi } from 'app/services/api';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import DocumentView from 'app/shared/components/document/DocumentView';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { emptyLayoutConfig } from 'constants/layout';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getDefaultErrorMessage } from 'utils/axios';
import { printFileFromBlob } from 'utils/file';
import { logger } from 'utils/logger';
import { downloadOrShareBlob } from 'utils/nativeFile';
import { openURLInNewTab } from 'utils/navigation';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { setSettingsAction, showMessageAction } from 'store/global/actions';
import {
  getAusFindingsListAction,
  resetAusFindingsListAction,
} from 'store/loanApplications/actions';
import { selectAusFindingsListOptionsByAusType } from 'store/loanApplications/selectors';

import { LoanHeadersTestIds } from '../LoanHeaders.testIds';

import { getMainHeaderStyles } from './MainHeader.styles';

const helmetProps = {
  title: 'View Document',
};

const AusFindings = ({ isInModal = false, typeOfAus, loanId, isLoading, setIsLoading }) => {
  const dispatch = useDispatch();

  const {
    applicationId,
    typeOfAus: typeOfAusParam,
    documentId: documentIdParam,
    loanId: loanIdParam,
  } = useParams();

  const ausFindingsListOptions = useSelector(
    selectAusFindingsListOptionsByAusType(typeOfAus ?? typeOfAusParam),
  );

  const [ausFindingOption, setAusFindingOption] = useState(null);

  const isDocumentActionsDisabled = isLoading || !ausFindingOption;

  const theme = useTheme();

  const classes = getMainHeaderStyles(theme, { isInModal });

  const loanParams = useMemo(() => ({ loanIdOrLegacyLoanNumber: loanId }), [loanId]);

  const requestData = useMemo(
    () =>
      isInModal
        ? {
            loanId,
            typeOfAus,
          }
        : {
            loanId: loanIdParam,
            typeOfAus: typeOfAusParam,
          },
    [isInModal, typeOfAus, typeOfAusParam, loanId, loanIdParam],
  );

  const { isViewAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_APPLICATION));

  useEffectSafeStrictMode(() => {
    if (!isViewAllowed) {
      return;
    }

    setIsLoading(true);
    dispatch(getAusFindingsListAction({ ...requestData, silent: true }))
      .catch(err => {
        logger.error(err);

        const isResponseNotFound = err?.response?.status === HTTP_RESPONSE_STATUS_CODES.NOT_FOUND;
        const message = isResponseNotFound ? 'AUS response not found' : getDefaultErrorMessage(err);

        dispatch(showMessageAction({ message, variant: 'error' }));
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, isViewAllowed, requestData, setIsLoading]);

  useEffect(() => {
    if (_.isEmpty(ausFindingsListOptions)) {
      return;
    }

    const preSelectedFindingOption = documentIdParam
      ? ausFindingsListOptions.find(({ value }) => value === documentIdParam)
      : ausFindingsListOptions[0];
    setAusFindingOption(preSelectedFindingOption);
  }, [ausFindingsListOptions, documentIdParam]);

  useEffect(() => {
    return () => {
      dispatch(resetAusFindingsListAction());
    };
  }, [dispatch]);

  const onDownloadDocument = useCallback(async () => {
    try {
      const { value: documentId, downloadName } = ausFindingOption;
      const { data: pdfBlob } = await documentsApi.getContent({ documentId });

      await downloadOrShareBlob(pdfBlob, downloadName);
    } catch (err) {
      logger.error(err);
      dispatch(showMessageAction({ message: 'Could not download document', variant: 'error' }));
    }
  }, [dispatch, ausFindingOption]);

  const onPrintDocument = useCallback(async () => {
    try {
      const documentId = ausFindingOption?.value;
      const { data: pdfBlob } = await documentsApi.getContent({ documentId });

      await printFileFromBlob(pdfBlob);
    } catch (err) {
      logger.error(err);
      dispatch(showMessageAction({ message: 'Could not print document', variant: 'error' }));
    }
  }, [dispatch, ausFindingOption?.value]);

  const onOpenDocumentInNewTab = useCallback(() => {
    const href = history.createHref({
      pathname: LoanAusFindingsPaths.loanAusFindings.generatePath({
        [AUS_FINDINGS_ROUTING_PARAMS.typeOfAus]: requestData.typeOfAus,
        [AUS_FINDINGS_ROUTING_PARAMS.loanId]: requestData.loanId,
        [AUS_FINDINGS_ROUTING_PARAMS.documentId]: ausFindingOption?.value,
      }),
    });

    openURLInNewTab(href);
  }, [requestData, ausFindingOption?.value]);

  const onHistoricalResultsChange = useCallback(
    (__, value) => setAusFindingOption(value),
    [setAusFindingOption],
  );

  const content = useMemo(
    () => (
      <Box sx={classes.root}>
        <AutocompleteCDS
          disableClearable
          className="ml-24 max-w-320"
          data-testid={LoanHeadersTestIds.ASSOCIATE_REPORT_FORM_HISTORICAL_RESULTS_SELECT}
          disabled={isLoading}
          label="Historical results"
          name="historicalResults"
          options={ausFindingsListOptions}
          value={ausFindingOption}
          onChange={onHistoricalResultsChange}
        />

        <DocumentView
          isView
          applicationId={applicationId}
          docId={ausFindingOption?.value}
          isControlVisible={false}
          isDownloadable={isDocumentActionsDisabled}
          isLoading={isLoading}
          isOpenInNewTabAllowed={!isDocumentActionsDisabled}
          isPrintable={!isDocumentActionsDisabled}
          loanParams={loanParams}
          setIsLoading={setIsLoading}
          onDownloadDocument={onDownloadDocument}
          onOpenDocumentInNewTab={onOpenDocumentInNewTab}
          onPrintDocument={onPrintDocument}
        />
      </Box>
    ),
    [
      isLoading,
      applicationId,
      setIsLoading,
      ausFindingOption,
      ausFindingsListOptions,
      onHistoricalResultsChange,
      onOpenDocumentInNewTab,
      onDownloadDocument,
      onPrintDocument,
      classes.root,
      isDocumentActionsDisabled,
      loanParams,
    ],
  );

  useEffect(() => {
    if (!isInModal) {
      dispatch(setSettingsAction({ layout: emptyLayoutConfig }));
    }
  }, [isInModal, dispatch]);

  if (isInModal) {
    return content;
  }

  if (!isViewAllowed && !isInModal) {
    return <NoAvailableMessage message="Sorry but we could not find available documents" />;
  }

  return <ContentLayout content={content} helmetProps={helmetProps} />;
};

AusFindings.propTypes = {
  ...WithLoadingPropTypes,
  isInModal: PropTypes.bool,
  loanId: PropTypes.string,
  typeOfAus: PropTypes.string,
};

export default withLoading(AusFindings);
